import Vue from 'vue'
import { useEventBus } from '@vueuse/core'
import axios from 'crypto-fetch/axios'
import { cancelPending } from '@ms/utils/cancel-pending.js'
import { useAuthStore } from '@ms/stores/auth'
import toast from 'fe-vui/toast'
import VDialog from 'fe-vui/dialog'
import { fetchBetBuilderOdds, fetchShareCode } from '@ms/api/betslip'
import { subSelection } from '@ms/components/betslip/core/push.js'
import { placeBet } from '@ms/components/betslip/placeBet.js'
import { useBetslipStore } from '@ms/stores/betslip'
import { MAX_BETSLIP_SELECTIONS_SIZE, ALL_MULTIPLE_SELECTIONS_LIMIT, MAX_BETSLIP_COMBOS } from '@ms/setting/betslip'
import {
  MESSAGE_OVER_LIMIT_COMBOS,
  BET_BUILDER_SELECTION_ABNORMAL,
  MESSAGE_SELECT_MAX_COUNT,
  MESSAGE_ALL_MULTIPLE_SELECTIONS_LIMIT,
  MESSAGE_OVER_MAX_COUNT_BET_BUILDER,
  MESSAGE_BETSLIP_COMBOS_LIMIT
} from '@ms/stores/betslip/config'
import { useBetslipBetBuilderStore } from '@ms/stores/betslip/betBuilder'
import { useBankerStore } from '@ms/stores/betslip/useBanker.js'
import {
  getCheckInfoBody,
  isRejectedSelection,
  isSelectedSelection,
  isBetBuilderSelection,
  convertSelectionOutcome,
  convertComboSelection,
  getBetBuilderSelectionId,
  convertBettableSelections,
  isBetBuilderSizeOverLimit,
  hasAbnormalBetBuilderSelection,
  getBetBuilderSizeSubSelectionSize,
  checkTempBetCount
} from '@ms/stores/betslip/func'
import { openLogin } from 'wap/utils/dialog-login'
import { updateSelectionEvent, updateSelectionMarket, updateSelectionOutcome } from '@ms/components/betslip/core/update.js'
import { saveLocalSelections } from '@ms/components/betslip/core/save.js'
import { updateSelectionsWithResponse, getPlaceBetBody } from '@ms/stores/betslip/parser.js'

export default {
  // 后台查询更新 selection 最新信息
  async checkCurrentSelections(recoverChange = false) {
    const { selectionList } = this
    const body = getCheckInfoBody(selectionList)

    if (!body.length) return

    try {
      const data = await axios.post('/facts-center/query/frontend/market-info/bet-slip-selection-info', body)
      updateSelectionsWithResponse(selectionList, data)
      if (recoverChange) {
        this.updateHasChange(false)
      }
    } catch (error) {
      console.error(error)
      // 不处理错误信息
      return Promise.resolve()
    }
  },

  // 后台查询指定 selection 最新信息
  async checkSelection(selection) {
    if (!selection) return
    const body = getCheckInfoBody([selection])

    try {
      const data = await axios.post('/facts-center/query/frontend/market-info/bet-slip-selection-info', body)
      updateSelectionsWithResponse(this.selectionList, data)
    } catch (error) {
      console.error(error)
      // 不处理错误信息
      return Promise.resolve()
    }
  },

  async addSelectionsFromShareCode(bettableBetSlip = [], shareCode = '') {
    const { selectionList, selectionNum } = this
    const newSelections = convertBettableSelections(bettableBetSlip)
    const validSelections = newSelections.filter((x) => selectionList.findIndex((item) => item.id === x.id) === -1)
    const count = validSelections.length
    const tempCount = selectionNum + count
    const tempBetCount = checkTempBetCount(validSelections, selectionList)

    if (tempBetCount > MAX_BETSLIP_COMBOS) {
      toast(MESSAGE_BETSLIP_COMBOS_LIMIT, { theme: 'dark' })
      return { exceed: true, cancelled: true }
    }

    if (tempCount > MAX_BETSLIP_SELECTIONS_SIZE || (useBetslipStore().isAllMultiplesMode && tempCount > ALL_MULTIPLE_SELECTIONS_LIMIT)) {
      return new Promise((resolve) => {
        VDialog({
          title: 'Note',
          message: MESSAGE_OVER_LIMIT_COMBOS,
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
          showClose: false,
          classNames: ['dialog-addBetslip-limitNote'],
        })
          .then(() => {
            this.confirmRebet({ list: newSelections, shareCode })
            resolve({ exceed: true })
            toast('Successfully loaded')
          })
          .catch(() => {
            resolve({ exceed: true, cancelled: true })
          })
      })
    } else {
      this.saveRebetSelections({ list: validSelections, shareCode })
      return { list: validSelections }
    }
  },

  /**
   * 根据ShareCode获取Betslip信息
   * @param  {String} shareCode   shareCode
   * @return {[type]}             [description]
   */
  async getShareBetslips(shareCode = '') {
    const data = await fetchShareCode(shareCode)
    return this.addSelectionsFromShareCode(data.bettableBetSlip, shareCode)
  },
  /**
   * 保存 rebet 的 selection 选项
   * @param {Array}  options.list       选项列表
   * @param {String} options.shareCode  share booking code
   */
  saveRebetSelections({ list, shareCode }) {
    const { selectionList, updateBetBuilderOdds } = this
    list.forEach((x) => {
      useEventBus('add-betslip-selection').emit(x)
    })
    const oldList = [...selectionList]
    list.forEach((x) => {
      if (isBetBuilderSelection(x)) {
        const index = oldList.findIndex((y) => y.id === x.id)
        if (index !== -1) {
          selectionList.splice(index, 1, x)
        } else {
          selectionList.push(x)
        }
      } else {
        selectionList.push(x)
      }
    })
    updateBetBuilderOdds()
    // 更新本地信息
    saveLocalSelections(selectionList)
    localStorage.setItem('sharecode', shareCode)
  },

  async placeOrder({ actualPayAmount, formSelections, voucher } = {}) {
    const { orderType, taxInfo } = this
    const body = getPlaceBetBody({ orderType, formSelections, voucher, actualPayAmount, taxInfo })

    return placeBet(body)
  },

  // type === 1, update event
  eventPushCallback(data) {
    try {
      data = JSON.parse(data)
      updateSelectionEvent(this.selectionList, data)
      saveLocalSelections(this.selectionList)
    } catch (error) {
      console.error('Event push callback failed:', error)
    }
  },

  // type === 2, update market
  marketPushCallback(data) {
    try {
      data = JSON.parse(data)
      updateSelectionMarket(this.selectionList, data)
      saveLocalSelections(this.selectionList)
    } catch (error) {
      console.error('Market push callback failed:', error)
    }
  },

  // type === 3, update odds
  oddsPushCallback(data) {
    try {
      data = JSON.parse(data)
      updateSelectionOutcome(this.selectionList, data, this.updateBetBuilderOdds)
      saveLocalSelections(this.selectionList)
    } catch (error) {
      console.error('Odds push callback failed:', error)
    }
  },

  // 添加/取消 selection, 传 id 表示取消 selection
  toggleSelection({ id, outcome, market, event, combos }) {
    try {
      let selection
      if (id) {
        selection = this.selectionList.find((x) => x.id === id)
      } else if (combos) {
        selection = convertComboSelection({ event, combos })
      } else {
        selection = convertSelectionOutcome({ outcome, market, event })
      }

      if (!selection) return false

      const shouldSelect = !isSelectedSelection(this.selectionList, selection)

      if (shouldSelect) {
        if (this.betBuilderSize) {
          if (hasAbnormalBetBuilderSelection(this.selectionList)) {
            toast(BET_BUILDER_SELECTION_ABNORMAL, { theme: 'dark' })
            return false
          }
        }

        const { selectionNum } = this
        const isBetBulder = isBetBuilderSelection(selection)
        const existSelectionInBetBuilder = isBetBulder && this.selectionList.find((x) => x.id === getBetBuilderSelectionId(event.eventId))

        if (existSelectionInBetBuilder) {
          const isAbnormal = existSelectionInBetBuilder.betBuilderSelections.some((x) => x.outcomeInfo.isSuspended || x.outcomeInfo.isUnavailable)
          if (isAbnormal) {
            toast('The current bet builder is unavailable now, please remove the relevant selections and try again.', { theme: 'dark' })
            return false
          }
        } else {
          const tempBetCount = checkTempBetCount([selection], this.selectionList)
          if (tempBetCount > MAX_BETSLIP_COMBOS) {
            toast(MESSAGE_BETSLIP_COMBOS_LIMIT, { theme: 'dark' })
            return false
          }
        }

        if (useBetslipStore().isAllMultiplesMode && selectionNum >= ALL_MULTIPLE_SELECTIONS_LIMIT) {
          toast(MESSAGE_ALL_MULTIPLE_SELECTIONS_LIMIT, { theme: 'dark' })
          return false
        }

        if (selectionNum >= MAX_BETSLIP_SELECTIONS_SIZE && !existSelectionInBetBuilder) {
          toast(MESSAGE_SELECT_MAX_COUNT, { theme: 'dark' })
          return false
        }
        if (isBetBuilderSizeOverLimit(this.selectionList, selection)) {
          toast(MESSAGE_OVER_MAX_COUNT_BET_BUILDER, { theme: 'dark' })
          return false
        }

        // add
        if (combos && getBetBuilderSizeSubSelectionSize(this.selectionList) > 0) {
          // 若betslip中已有本场比赛的bet builder选项，會先去除betslip 內當下比賽之前所選的 bet builder selections, 再以 combo 的 Selection 內容覆蓋，令betslip 內當下比賽的bet builder selections 只有用戶所選的combo 內的selection
          const id = getBetBuilderSelectionId(event.eventId)
          const currentSelection = this.selectionList.find((x) => x.id === id)
          if (currentSelection) {
            this.removeSelection(currentSelection)
            useBetslipBetBuilderStore().removeBetBuilderLoading(id)
          }
        }
        this.addSelection(selection)
        this.checkSelection(selection).then(() => {
          if (selection && selection.betBuilderSelections) {
            const isAbnormal = selection.betBuilderSelections.some((x) => x.outcomeInfo.isSuspended || x.outcomeInfo.isUnavailable)
            if (isAbnormal) {
              toast('The current bet builder is unavailable, please delete it and recombine.', { theme: 'dark' })
              return false
            }
          }
        })

        if (isBetBulder) {
          const targetSelection = this.selectionList.find((x) => x.id === selection.id)
          this.updateSingleBetBuilderOdds(targetSelection, !!combos).then((data) => {
            if (data && data.isSame) {
              toast('This selection cannot increase the odds of your bet builder.', { theme: 'dark' })
            }
          })
        }
      } else {
        // remove
        const betslipStore = useBetslipStore()
        betslipStore.removeRejectSelections(selection)
        this.removeSelection(selection, !!combos)
        betslipStore.removeSingleStake(id)
        useBetslipBetBuilderStore().removeBetBuilderLoading(id)
      }

      const pushCallbackList = [this.eventPushCallback, this.marketPushCallback, this.oddsPushCallback]

      pushCallbackList.forEach((callback, index) => {
        const type = index + 1
        // 订阅/取消订阅 event/market/odds
        subSelection(selection, shouldSelect, callback, type)
      })

      return shouldSelect
    } catch (error) {
      console.error(error)
      return false
    }
  },

  removeRejectSelections(rejectSelections = []) {
    if (!Array.isArray(rejectSelections)) return
    const { selectionList } = this
    rejectSelections.forEach((target) => {
      selectionList.forEach((x) => {
        const matched = isRejectedSelection(x, target)
        if (matched) {
          this.toggleSelection({ id: x.id, active: false })
        }
      })
    })
  },

  // remove all
  async removeAllSelections() {
    const list = [...this.selectionList]
    const pushCallbackList = [this.eventPushCallback, this.marketPushCallback, this.oddsPushCallback]
    list.forEach((selection) => {
      pushCallbackList.forEach((callback, index) => {
        const type = index + 1
        // 取消订阅 event/market/odds
        subSelection(selection, false, callback, type)
      })
    })
    this.updateRemoveAllSelections()
    useBetslipStore().resetCacheStake()
    useBetslipStore().resetBetslip()
    useBankerStore().$reset()
  },

  // betslip 模块赛事添加/取消订阅
  subBetslip(isSub = true) {
    const { selectionList } = this
    if (selectionList.length > 0) {
      const pushCallbackList = [this.eventPushCallback, this.marketPushCallback, this.oddsPushCallback]
      selectionList.forEach((selection) => {
        pushCallbackList.forEach((callback, index) => {
          const type = index + 1
          // 订阅/取消订阅 event/market/odds
          subSelection(selection, isSub, callback, type)
        })
      })
    }
  },

  // 更新 has change 状态
  updateHasChange(status = true) {
    const list = [...this.selectionList]
    const len = list.length
    for (let index = 0; index < len; index++) {
      const selection = list[index]
      if (isBetBuilderSelection(selection)) {
        if (status === 0 && selection.outcomeInfo.isUnavailable) {
          if (!useAuthStore().loginStatus) {
            openLogin()
            return
          }
          this.removeSelection(selection)
        } else {
          const tempList = []
          selection.betBuilderSelections.forEach((subSelection) => {
            const { isSuspended, isUnavailable } = subSelection.outcomeInfo
            // 强制删除
            if (status === 0 && (isSuspended || isUnavailable)) {
              tempList.push([selection.id, subSelection.id])
            }
          })
          if (tempList.length > 0) {
            if (!useAuthStore().loginStatus) {
              openLogin()
              return
            }
            tempList.forEach((x) => {
              this.removeBetBuilderSelection(x[0], x[1])
            })
          }
        }
      } else {
        const { isSuspended, isUnavailable } = selection.outcomeInfo
        // 强制删除
        if (status === 0 && (isSuspended || isUnavailable)) {
          this.removeSelection(selection)
        }
      }
    }
    useBetslipStore().updateSelectionChanged(status)
  },

  /**
   * 确认将 rebet 的 betslip 选项加入 selection 列表
   * @param {Array}  options.list       选项列表
   * @param {String} options.shareCode  share booking code
   */
  async confirmRebet({ list, shareCode }) {
    await this.removeAllSelections()
    this.saveRebetSelections({ list, shareCode })
  },

  async updateSingleBetBuilderOdds(selection, isCombos = false) {
    if (!selection) return
    if (!useAuthStore().loginStatus) {
      const unwatch = useEventBus('LOGIN_SUCCESS').on(() => {
        this.updateSingleBetBuilderOdds(selection, isCombos)
        unwatch()
      })
      return
    }

    const { setBetBuilderLoading, setBetBuilderSize } = useBetslipBetBuilderStore()
    const { id, betBuilderSelections } = selection
    const size = betBuilderSelections.length
    const uniqId = betBuilderSelections.reduce((acc, cur) => acc + cur.id, '')

    if (isCombos) {
      setBetBuilderSize(id, size, uniqId)
      return
    }
    if (size === 1) {
      cancelPending.cancel(`${selection.eventId}/bet-builder/odds`)
      setBetBuilderSize(id, size, uniqId)
      const data = betBuilderSelections[0].outcomeInfo
      Vue.set(selection.outcomeInfo, 'odds', data.odds)
      Vue.set(selection.outcomeInfo, 'probability', data.probability)
      return
    }

    setBetBuilderLoading(id, true, uniqId)
    try {
      const data = await fetchBetBuilderOdds(selection)
      if (data) {
        setBetBuilderSize(id, size)
        Vue.set(selection.outcomeInfo, 'isUnavailable', false)
        if (data.odds === selection.outcomeInfo.odds) {
          setBetBuilderLoading(id, false)
          return { isSame: true }
        } else {
          Vue.set(selection.outcomeInfo, 'odds', data.odds)
          Vue.set(selection.outcomeInfo, 'probability', data.probability)
        }
      }
    } catch (e) {
      if (e.bizCode === 19000) {
        Vue.set(selection.outcomeInfo, 'isUnavailable', true)
        toast(e.message, { theme: 'dark' })
      }
      console.error(e)
    }
    setBetBuilderLoading(id, false)
  },

  updateBetBuilderOdds() {
    const { selectionList, updateSingleBetBuilderOdds } = this
    selectionList.forEach((selection) => {
      if (isBetBuilderSelection(selection)) {
        updateSingleBetBuilderOdds(selection)
      }
    })
  }
}
